/* eslint-disable import/no-cycle */
import { LocationDescriptor } from 'history';
import {
  getDemoAccountToken,
  getToken,
  removeDemoAccountToken,
  removeToken,
  setDemoAccountToken,
  setToken,
} from './token';
import { resetState } from './reset-state';
import { redirectWithoutRefresh } from './redirect';
import loader from '../loader';
import api from '../../api';
import toaster from '../toaster';
import { AnalyticsEvents } from '../enums/analytics';
import { clearTestEmailAddressFromLocalStorage } from '../../components/sequence/shared/modals/email-modal/helpers';
import { removeProspectsFiltersFromLocalStore } from '../../components/prospect/helpers/prospect-filters-resolver';
import { removeEmailInboxFiltersFromLocalStore } from '../../components/unified-inbox/helpers/utils/email-inbox-filters-local-storage-handler';
import { removeCobaltSessionToken } from '../../components/settings/components/integrations/utils/helper';
import { removeSequenceFilterFromLocalStorage } from '../../components/sequence/components/sequence-list/helper';
import {
  removeEmailReportsFilters,
  removeSelectedColumnsFromLocalStorage,
  removeSequenceReportsFilters,
  removeTeamPerformanceFilters,
} from '../../components/reports/helpers/helper';
import {
  removeSearchFilterFromLocalStore,
  removeTasksFiltersFromLocalStore,
  removeTaskTypeFiltersInLocalStore,
} from '../../components/tasks/components/tasks-content/utils/helper';
import { removeCustomScript } from './initialize-custom-script';
import { removeProspectTableColumnsFromLocalStorage } from '../../components/prospect/components/prospect-table/utils/helper';
import {
  removeDemoAccountEnteredTimestamp,
  removeUserEmailFromStore,
  setDemoAccountEnteredTimestamp,
} from '../../components/home/utils/helper';
import { clearAllEmailAccountInfrastructureKeysFromLocalStorage } from '../../components/email-accounts/helpers/email-accounts-infra-local-storage';
import { removeAddEmailAccountsDataFromLS } from '../../components/email-accounts/components/add-email-accounts/helpers/helpers';
import { removeIsSidebarInfoShown } from '../../components/home/components/sidebar/utils/helper';
import {
  removeDemoAccountModuleViewed,
  removeIsDemoFeedbackSubmitted,
  setDemoAccountEntryPoint,
} from './demo-account-handlers';
import { resetUserIdentification } from './initialize-third-party-integrations';
import {
  clearLeadCompanyProfileModalSize,
  clearLeadProfileModalSize,
  clearColumnPreferences as clearV2LeadFinderColumnPreferences,
} from '../../components/v2-lead/utils/column-preferences';
import { resetCompanyColumnPreferences as clearV2CompanyColumnPreferences } from '../../components/v2-lead/utils/company-column-preferences';
import {
  clearCompanyIdNameMap,
  clearCompanyProfileModalSize,
} from '../../components/v2-lead/utils/company-id-name-map';
import { clearRecentSearches as clearV2LeadFinderRecentSearches } from '../../components/v2-lead/utils/recent-searches';
import { destroyTwilioDevice } from './twilio-device-singleton';

export type LoginOptions = {
  redirect?: boolean;
  token: string;
  redirectUrl?: string;
  isDemoAccount?: boolean;
};

export type LocationDescriptorOptions = LocationDescriptor & {
  pathname?: string;
};

export type LogoutOptions = {
  redirect?: boolean;
  pathName?: string;
  fallbackTo?: LocationDescriptorOptions;
  query?: string;
  isBackToMainSHDomain?: boolean;
};

type FallbackUrlState = {
  from: LocationDescriptorOptions;
};

const deleteTokenAndRedirect = ({
  redirect = true,
  pathName = '/login',
  fallbackTo,
  query,
  isBackToMainSHDomain,
}: LogoutOptions = {}) => {
  destroyTwilioDevice(true);
  removeToken();
  removeDemoAccountToken();
  removeDemoAccountModuleViewed();
  removeIsDemoFeedbackSubmitted();
  resetState();
  // cleanup
  loader.hide();

  const signupRoute = '/signup';
  const redirectURL =
    fallbackTo && fallbackTo.pathname === signupRoute ? signupRoute : pathName;

  if (isBackToMainSHDomain) {
    window.open(process.env.REACT_APP_BASE_URL, '_self');

    return;
  }

  if (redirect) {
    redirectWithoutRefresh<FallbackUrlState>({
      pathname: redirectURL,
      state: { from: fallbackTo },
      search: query,
    });
  }
};

const clearLocalStorage = () => {
  // Delete test_email_address
  clearTestEmailAddressFromLocalStorage();

  // Removing filters from local store
  removeProspectsFiltersFromLocalStore();
  removeEmailInboxFiltersFromLocalStore();
  removeSequenceFilterFromLocalStorage();

  // Delete cobalt token from local store
  removeCobaltSessionToken();

  // Remove Prospect Table Columns
  removeProspectTableColumnsFromLocalStorage();

  // Remove task filters from local storage
  removeTasksFiltersFromLocalStore();
  removeTaskTypeFiltersInLocalStore();
  removeSearchFilterFromLocalStore();

  removeTeamPerformanceFilters();

  // Remove Sequence Reports filters
  removeSequenceReportsFilters();

  // Remove User Email Address
  removeUserEmailFromStore();

  // Remove Email Infra Cart Details
  clearAllEmailAccountInfrastructureKeysFromLocalStorage();

  // Remove Add Email Accounts Data
  removeAddEmailAccountsDataFromLS();

  // Remove Is Sidebar Info Shown Key
  removeIsSidebarInfoShown();

  // Remove Email Reports Filters
  removeEmailReportsFilters();

  // Remove Selected Columns from Local Storage
  removeSelectedColumnsFromLocalStorage();

  // Remove V2 Lead Finder Column Preferences
  clearV2LeadFinderColumnPreferences();

  // Remove V2 Company Column Preferences
  clearV2CompanyColumnPreferences();

  // Remove V2 Lead Finder Recent Searches
  clearV2LeadFinderRecentSearches();

  // Remove V2 Lead Finder Company ID-Name Map
  clearCompanyIdNameMap();

  clearLeadProfileModalSize();
  clearLeadCompanyProfileModalSize();
  clearCompanyProfileModalSize();
};

export class AuthHelper {
  static get isAuthenticated(): boolean {
    const searchParams = new URLSearchParams(window.location.search);
    const demoToken = searchParams.get('demoToken');
    const entryPoint = searchParams.get('entryPoint');
    if (demoToken) {
      setDemoAccountToken(demoToken);
      setDemoAccountEnteredTimestamp();
      if (entryPoint) {
        setDemoAccountEntryPoint(entryPoint);
      }
      return true;
    }

    const token = new URLSearchParams(window.location.search).get('token');
    if (token) {
      setToken(token);
      return true;
    }

    const isTokenPresent = !!getDemoAccountToken() || !!getToken();
    if (isTokenPresent) {
      return isTokenPresent;
    }

    return false;
  }

  static login({ redirect = true, token, redirectUrl = '' }: LoginOptions) {
    setToken(token);

    if (redirect) {
      redirectWithoutRefresh(redirectUrl || '/');
    }
  }

  static async logout({
    redirect = true,
    pathName,
    fallbackTo,
    query = '',
    isBackToMainSHDomain = false,
  }: LogoutOptions = {}) {
    if (getToken()) {
      api
        .get('/auth/logout', {
          headers: { authorization: `Bearer ${getToken()}` },
        })
        .then(() => {
          toaster.success('Successfully logged out!');

          // Analytics Track this event
          window.analytics?.track?.(AnalyticsEvents.SignedOut);
        })
        .catch(() => {
          // eslint-disable-next-line no-console
          console.log('Session expired');
        });
    }

    // Reset user identification for analytics to prevent anonymous events
    resetUserIdentification();

    removeCustomScript();

    deleteTokenAndRedirect({
      redirect,
      pathName,
      fallbackTo,
      query,
      isBackToMainSHDomain,
    });

    // if you want to clear any data from LocalStorage
    // then add that handler function in the below defined function
    clearLocalStorage();
    // DO NOT ADD CLEANUP FUNCTIONS OUTSIDE OF THE ABOVE FUNCTION
  }

  static async logoutDemoAccount({
    shouldRedirect = true,
  }: {
    shouldRedirect?: boolean;
  } = {}) {
    removeDemoAccountToken();
    removeDemoAccountModuleViewed();
    removeIsDemoFeedbackSubmitted();
    removeDemoAccountEnteredTimestamp();
    resetState();

    // Reset user identification for analytics to prevent anonymous events
    resetUserIdentification();

    loader.hide();

    // if you want to clear any data from LocalStorage
    // then add that handler function in the below defined function
    clearLocalStorage();
    // DO NOT ADD CLEANUP FUNCTIONS OUTSIDE OF THE ABOVE FUNCTION

    if (shouldRedirect) {
      window.open(
        `${process.env.REACT_APP_BASE_URL}/signup?utm_source=demoaccount`,
      );
      window.close();
    }
  }

  static redirectOnAccountDeletion(pathToRedirect: string) {
    // Reset user identification for analytics to prevent anonymous events
    resetUserIdentification();

    removeCustomScript();

    deleteTokenAndRedirect({
      redirect: true,
      pathName: pathToRedirect,
      query: '',
      isBackToMainSHDomain: false,
    });

    // if you want to clear any data from LocalStorage
    // then add that handler function in the below defined function
    clearLocalStorage();
    // DO NOT ADD CLEANUP FUNCTIONS OUTSIDE OF THE ABOVE FUNCTION
  }
}

export const getLeadType = (str = '') => {
  switch (str) {
    case 'social':
      return 'link';
    case 'name':
      return 'name';
    case 'role':
      return 'jobTitle';
    case 'skill':
      return 'skills';
    default:
      return 'link';
  }
};

export const generateSerpLeadRedirectUrl = (str = '') => {
  if (str?.includes('&type=')) {
    const [url, type] = str?.split('&type=');

    return `/leads?start=1&take=25&${getLeadType(type || '')}=${url || ''}`;
  }

  return `/leads?start=1&take=25&link=${str}`;
};
